/**
 * Module Access Utilities
 *
 * Context and hooks for per-module permission enforcement.
 * Used by ModuleGate (route guard), Sidebar (nav filtering),
 * and RequirePermission (CanEdit / ReadOnlyBadge).
 */

import { createContext, useContext } from 'react';
import { resolveModuleAccess, type ModuleId, type ModuleAccess } from './permissions';
import { useAuthStore } from '@store/authStore';

export type { ModuleId, ModuleAccess };

/**
 * Injected by ModuleGate into the subtree of each gated route.
 * Defaults to 'edit' so all CanEdit usage outside a ModuleGate
 * continues to work purely on global role (backwards compatible).
 */
export const ModuleAccessContext = createContext<ModuleAccess>('edit');

/**
 * Read the effective access level injected by the nearest ModuleGate.
 * Returns 'edit' when used outside a ModuleGate.
 */
export function useModuleContext(): ModuleAccess {
  return useContext(ModuleAccessContext);
}

/**
 * Returns the effective access level for every module at once.
 * Used by Sidebar to filter nav items.
 */
export function useAllModuleAccess(): Record<ModuleId, ModuleAccess> {
  const { teamRole, modulePermissions } = useAuthStore();

  const modules: ModuleId[] = [
    'dashboard',
    'people',
    'organisations',
    'volunteers',
    'assets',
    'reports',
    'finance',
  ];

  return Object.fromEntries(
    modules.map((m) => [m, resolveModuleAccess(m, teamRole, modulePermissions)])
  ) as Record<ModuleId, ModuleAccess>;
}
